import { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import AppToast from '../../../../shared/components/AppToast';
import useFormState from '../../../../shared/hooks/useFormState';
import { ensureCatalogAuth } from '../../../../services/backend/authService';
import { getAgregarUsuario, getEditarUsuario, grabarUsuario } from '../../../../services/backend/adminService';
import logger from '../../../../shared/utils/logger';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const UsuarioFormDialog = ({ visible, mode = "agregar", usuario, onCancel, onSave }) => {
    const isEdit = mode === "editar";
    const toastRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [perfiles, setPerfiles] = useState([]);
    const [almacenes, setAlmacenes] = useState([]);

    const { values, errors, setField, reset, validate } = useFormState(
        {
            id: null, codigo: '', nombre: '', usuarioLogin: '', contrasena: '',
            email: '', telefono: '', perfil: '', almacen: '', canal: '',
            recibeEmail: false, activo: "1",
        },
        {
            codigo: [(v) => (!isEdit && !v?.trim() ? "Requerido." : null)],
            nombre: [(v) => (!v?.trim() ? "Requerido." : null)],
            usuarioLogin: [(v) => (!v?.trim() ? "Requerido." : null)],
            contrasena: [(v) => (!isEdit && !v?.trim() ? "Requerido." : null)],
            perfil: [(v) => (!v ? "Requerido." : null)],
            email: [(v) => {
                if (v?.trim() && !EMAIL_REGEX.test(v.trim())) return "Email inválido.";
                return null;
            }],
            telefono: [(v) => {
                if (v?.trim() && !/^\d{8,11}$/.test(v.trim())) return "Debe tener entre 8 y 11 dígitos.";
                return null;
            }],
        }
    );

    useEffect(() => {
        if (!visible) return;
        if (isEdit && usuario?.id) {
            fetchUsuario(usuario.id);
        } else if (!isEdit) {
            fetchFormData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, mode, usuario?.id]);

    const fetchFormData = async () => {
        setLoading(true);
        try {
            await ensureCatalogAuth();
            const response = await getAgregarUsuario();
            if (response) {
                const perfilesData = Array.isArray(response.perfiles) ? response.perfiles : [];
                setPerfiles(perfilesData.map(p => ({ label: p.descripcion, value: String(p.id) })));
                const almacenesData = Array.isArray(response.almacenes) ? response.almacenes : [];
                setAlmacenes(almacenesData.map(a => ({ label: `${a.sucursal} (${a.almacen})`, value: a.almacen })));
            }
        } catch (err) {
            logger.error("Error al cargar datos:", err);
            toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al cargar datos del formulario', life: 5000 });
        } finally {
            setLoading(false);
        }
    };

    const fetchUsuario = async (userId) => {
        setLoading(true);
        try {
            await ensureCatalogAuth();
            const response = await getEditarUsuario(userId);
            if (response) {
                const perfilesData = Array.isArray(response.perfiles) ? response.perfiles : [];
                setPerfiles(perfilesData.map(p => ({ label: p.descripcion, value: String(p.id) })));
                const almacenesData = Array.isArray(response.almacenes) ? response.almacenes : [];
                setAlmacenes(almacenesData.map(a => ({ label: `${a.sucursal} (${a.almacen})`, value: a.almacen })));
                reset({
                    id: response.usuario.id || null,
                    codigo: response.usuario.codigo || '',
                    nombre: response.usuario.nombre || '',
                    usuarioLogin: response.usuario.usuario || '',
                    contrasena: response.usuario.contrasena || '',
                    email: response.usuario.email || '',
                    telefono: response.usuario.telefono || '',
                    perfil: response.usuario.perfil?.id || response.usuario.perfil || '',
                    almacen: response.almacen || '',
                    canal: response.canal || '',
                    recibeEmail: response.usuario.recibeEmail || false,
                    activo: response.usuario.activo !== false ? "1" : "0",
                });
            }
        } catch (err) {
            logger.error("Error al cargar usuario:", err);
            toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Error al cargar datos del usuario', life: 5000 });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await ensureCatalogAuth();
            await grabarUsuario({
                id: isEdit ? String(values.id || '') : '',
                codigo: String(values.codigo || ''),
                nombre: values.nombre,
                usuario: values.usuarioLogin,
                contrasena: values.contrasena,
                email: values.email,
                telefono: values.telefono,
                perfil: String(values.perfil || ''),
                almacen: String(values.almacen || ''),
                canal: String(values.canal || ''),
                activo: String(values.activo || '1')
            });
            toastRef.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: isEdit ? 'Usuario actualizado correctamente' : 'Usuario guardado correctamente',
                life: 3000
            });
            if (!isEdit) reset({
                id: null, codigo: '', nombre: '', usuarioLogin: '', contrasena: '',
                email: '', telefono: '', perfil: '', almacen: '', canal: '',
                recibeEmail: false, activo: "1",
            });
            onCancel();
            if (onSave) await onSave();
        } catch (err) {
            logger.error("Error al guardar usuario:", err);
            toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Error al guardar usuario', life: 5000 });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        reset({
            id: null, codigo: '', nombre: '', usuarioLogin: '', contrasena: '',
            email: '', telefono: '', perfil: '', almacen: '', canal: '',
            recibeEmail: false, activo: "1",
        });
        onCancel();
    };

    return (
        <Dialog
            header={isEdit ? "Editar Usuario" : "Agregar Usuario"}
            visible={visible}
            style={{ width: '52rem', maxWidth: '95vw' }}
            onHide={handleCancel}
            loading={loading}
        >
            <AppToast ref={toastRef} />
            <div className="pt-3">
                {!isEdit && (
                    <div className="formgrid grid align-items-center mb-4">
                        <label htmlFor="codigo" className="col-12 md:col-3 text-lg">Código *</label>
                        <div className="col-12 md:col-9">
                            <InputText id="codigo" value={values.codigo} onChange={(e) => setField('codigo', e.target.value)} className={`w-full ${errors.codigo ? 'p-invalid' : ''}`} />
                            {errors.codigo && <small className="p-error">{errors.codigo}</small>}
                        </div>
                    </div>
                )}
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="nombre" className="col-12 md:col-3 text-lg">Nombre *</label>
                    <div className="col-12 md:col-9">
                        <InputText id="nombre" value={values.nombre} onChange={(e) => setField('nombre', e.target.value)} className={`w-full ${errors.nombre ? 'p-invalid' : ''}`} />
                        {errors.nombre && <small className="p-error">{errors.nombre}</small>}
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="email" className="col-12 md:col-3 text-lg">Email</label>
                    <div className="col-12 md:col-9">
                        <InputText id="email" value={values.email} onChange={(e) => setField('email', e.target.value)} className={`w-full ${errors.email ? 'p-invalid' : ''}`} />
                        {errors.email && <small className="p-error">{errors.email}</small>}
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="telefono" className="col-12 md:col-3 text-lg">Teléfono</label>
                    <div className="col-12 md:col-9">
                        <InputText id="telefono" value={values.telefono} onChange={(e) => setField('telefono', e.target.value.replace(/\D/g, ''))} className={`w-full ${errors.telefono ? 'p-invalid' : ''}`} />
                        {errors.telefono && <small className="p-error">{errors.telefono}</small>}
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="usuario" className="col-12 md:col-3 text-lg">Usuario *</label>
                    <div className="col-12 md:col-9">
                        <InputText id="usuario" value={values.usuarioLogin} onChange={(e) => setField('usuarioLogin', e.target.value)} className={`w-full ${errors.usuarioLogin ? 'p-invalid' : ''}`} />
                        {errors.usuarioLogin && <small className="p-error">{errors.usuarioLogin}</small>}
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="contrasena" className="col-12 md:col-3 text-lg">Contraseña{isEdit ? '' : ' *'}</label>
                    <div className="col-12 md:col-9">
                        <InputText id="contrasena" value={values.contrasena} onChange={(e) => setField('contrasena', e.target.value)} type="password" className={`w-full ${errors.contrasena ? 'p-invalid' : ''}`} />
                        {errors.contrasena && <small className="p-error">{errors.contrasena}</small>}
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="perfil" className="col-12 md:col-3 text-lg">Perfil *</label>
                    <div className="col-12 md:col-9">
                        <Dropdown id="perfil" value={String(values.perfil)} options={perfiles} onChange={(e) => setField('perfil', e.value)} placeholder="Seleccionar perfil" className={`w-full ${errors.perfil ? 'p-invalid' : ''}`} />
                        {errors.perfil && <small className="p-error">{errors.perfil}</small>}
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="almacen" className="col-12 md:col-3 text-lg">Almacén</label>
                    <div className="col-12 md:col-9">
                        <Dropdown id="almacen" value={String(values.almacen)} options={almacenes} onChange={(e) => setField('almacen', e.value)} placeholder="Seleccionar almacén" className="w-full" />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="canal" className="col-12 md:col-3 text-lg">Canal</label>
                    <div className="col-12 md:col-9">
                        <Dropdown id="canal" value={values.canal} options={[
                            { label: "Sin canal", value: "" },
                            { label: "Mayorista", value: "MAYORISTA" },
                            { label: "Retail", value: "RETAIL" }
                        ]} onChange={(e) => setField('canal', e.value)} placeholder="Seleccionar canal" className="w-full" />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="recibeEmail" className="col-12 md:col-3 text-lg">Recibe notificaciones</label>
                    <div className="col-12 md:col-9">
                        <InputSwitch id="recibeEmail" checked={values.recibeEmail} onChange={(e) => setField('recibeEmail', e.value)} />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-5">
                    <label htmlFor="activo" className="col-12 md:col-3 text-lg">Activo</label>
                    <div className="col-12 md:col-9">
                        <InputSwitch id="activo" checked={values.activo === "1"} onChange={(e) => setField('activo', e.value ? "1" : "0")} />
                    </div>
                </div>
                <div className="flex justify-content-end gap-2">
                    <Button className="p-button-text" label="cancelar" onClick={handleCancel} disabled={saving} />
                    <Button className="p-button" label="grabar" onClick={handleSave} loading={saving} disabled={saving} />
                    
                </div>
            </div>
        </Dialog>
    );
};
